import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import React, { useState, useRef } from "react";
import { INVOICE_TYPES } from "../../constants";
import { InvoiceItemSize12, InvoiceItemSize4 } from "./Common";
import { If, Then, Else } from "react-if";

import "./invoice.css";
import InvoiceMaterialEditTable from "./InvoiceMaterialEditTable";
import { TextareaAutosize } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import logo from "../../logo_blue.svg";

export default function InvoiceUSA({
  materialShippingDetails,
  editMode,
  onChange,
  invoiceGeneralData,
}) {
  const { t } = useTranslation();
  const [shipperCopied, setShipperCopied] = useState(false);
  const [shipmentCopied, setShipmentCopied] = useState(false);
  const [recipientCopied, setRecipientCopied] = useState(false);
  const [brokerCopied, setBrokerCopied] = useState(false);
  const [importerCopied, setImporterCopied] = useState(false);
  const [specialInstructionsCopied, setSpecialInstructionsCopied] = useState(false);
  const [materialCopied, setMaterialCopied] = useState(false);
  const [commentCopied, setCommentCopied] = useState(false);
  let totalValue = materialShippingDetails?.reduce((a, item) => {
    const price = Number.isSafeInteger(Number(item.cost))
      ? Number(item.cost)
      : 0;
    return Number(a) + price;
  }, 0);

  totalValue = totalValue + Number(invoiceGeneralData.shipping_cost);
  const handleCopyMaterial = async () => {
  const rows = materialShippingDetails || [];
  const shipText = invoiceGeneralData?.shippingcost_text || "";
  const shipCost = invoiceGeneralData?.shipping_cost || 0;
  const currency = invoiceGeneralData?.currency_code || "";
  const total = totalValue || 0;

  // ---------------------------------------------------------
  // FORMAT 1: PLAIN TEXT (For Notepad)
  // Splits multi-line text and "draws" the table row-by-row 
  // so the description is stacked, but columns remain perfectly aligned.
  // ---------------------------------------------------------
  
  // Helper to safely split text into an array of individual lines
  const getLines = (str) => String(str || "").replace(/\r/g, "").split("\n").map(l => l.trim());

  // Convert all data into arrays of lines
  const textData = rows.map(r => ({
    q: getLines(r.quantity),
    d: getLines(r.description),
    c: getLines(r.country_of_origin),
    v: getLines(r.cost)
  }));

  const sTextLines = getLines(shipText);
  const sCostLines = getLines(shipCost);
  const tTextLines = getLines(`Total Value (${currency})`);
  const tCostLines = getLines(total);

  // Helper to calculate the maximum width needed for a column
  const calcWidth = (header, dataLinesArray) => {
    let max = header.length;
    dataLinesArray.forEach(lines => {
      lines.forEach(l => { if (l.length > max) max = l.length; });
    });
    return max + 4; // Add 4 spaces of padding between columns
  };

  const w1 = calcWidth("NET QUANTITY", textData.map(r => r.q));
  const w2 = calcWidth("ITEM DESCRIPTION AND ELANCO ITEM CODE", [...textData.map(r => r.d), sTextLines, tTextLines]);
  const w3 = calcWidth("COO", textData.map(r => r.c));
  const w4 = calcWidth("CUSTOMS VALUE", [...textData.map(r => r.v), sCostLines, tCostLines]);

  let plainText = 
    "NET QUANTITY".padEnd(w1) + 
    "ITEM DESCRIPTION AND ELANCO ITEM CODE".padEnd(w2) + 
    "COO".padEnd(w3) + 
    "CUSTOMS VALUE".padEnd(w4) + "\r\n";

  // Build the text grid line by line
  textData.forEach(r => {
    // Find how many lines tall this specific row needs to be
    const maxLines = Math.max(r.q.length, r.d.length, r.c.length, r.v.length);
    
    for (let i = 0; i < maxLines; i++) {
      const qStr = r.q[i] || "";
      const dStr = r.d[i] || "";
      const cStr = r.c[i] || "";
      const vStr = r.v[i] || "";
      plainText += qStr.padEnd(w1) + dStr.padEnd(w2) + cStr.padEnd(w3) + vStr.padEnd(w4) + "\r\n";
    }
    // Add a blank line between rows to make multi-line descriptions easier to read
    plainText += "\r\n"; 
  });

  // Align shipping under the correct columns
  const maxShip = Math.max(sTextLines.length, sCostLines.length);
  for (let i = 0; i < maxShip; i++) {
    plainText += "".padEnd(w1) + (sTextLines[i] || "").padEnd(w2) + "".padEnd(w3) + (sCostLines[i] || "").padEnd(w4) + "\r\n";
  }

  // Align totals under the correct columns
  const maxTot = Math.max(tTextLines.length, tCostLines.length);
  for (let i = 0; i < maxTot; i++) {
    plainText += "".padEnd(w1) + (tTextLines[i] || "").padEnd(w2) + "".padEnd(w3) + (tCostLines[i] || "").padEnd(w4) + "\r\n";
  }


  // ---------------------------------------------------------
  // FORMAT 2: MICROSOFT-OPTIMIZED HTML (For Excel, Word, Outlook)
  // ---------------------------------------------------------
  const formatForHtml = (str) => {
    let escaped = String(str || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return escaped.replace(/\r?\n/g, "<br style='mso-data-placement:same-cell;' />");
  };

  let html = `<table style="border-collapse:collapse; font-family:Arial, sans-serif; font-size:13px; width:100%;">`;
  html += `<thead><tr>`;
  html += `<th style="border:1px solid #ccc; padding:8px; text-align:left; background-color:#dbeafe;">NET QUANTITY</th>`;
  html += `<th style="border:1px solid #ccc; padding:8px; text-align:left; background-color:#dbeafe;">ITEM DESCRIPTION AND ELANCO ITEM CODE</th>`;
  html += `<th style="border:1px solid #ccc; padding:8px; text-align:left; background-color:#dbeafe;">COO</th>`;
  html += `<th style="border:1px solid #ccc; padding:8px; text-align:left; background-color:#dbeafe;">CUSTOMS VALUE</th>`;
  html += `</tr></thead><tbody>`;
  
  rows.forEach(r => {
    html += `<tr>`;
    html += `<td style="border:1px solid #ccc; padding:8px; vertical-align:top;">${formatForHtml(r.quantity)}</td>`;
    html += `<td style="border:1px solid #ccc; padding:8px; vertical-align:top; white-space:pre-wrap;">${formatForHtml(r.description)}</td>`;
    html += `<td style="border:1px solid #ccc; padding:8px; vertical-align:top;">${formatForHtml(r.country_of_origin)}</td>`;
    html += `<td style="border:1px solid #ccc; padding:8px; vertical-align:top;">${formatForHtml(r.cost)}</td>`;
    html += `</tr>`;
  });

  html += `<tr>`;
  html += `<td colspan="3" style="border:1px solid #ccc; padding:8px; text-align:right;">${formatForHtml(shipText)}</td>`;
  html += `<td style="border:1px solid #ccc; padding:8px; vertical-align:top;">${formatForHtml(shipCost)}</td>`;
  html += `</tr><tr>`;
  html += `<td colspan="3" style="border:1px solid #ccc; padding:8px; text-align:right; font-weight:bold;">Total Value (${formatForHtml(currency)})</td>`;
  html += `<td style="border:1px solid #ccc; padding:8px; font-weight:bold; vertical-align:top;">${formatForHtml(total)}</td>`;
  html += `</tr></tbody></table>`;

  // ---------------------------------------------------------
  // EXECUTE COPY
  // ---------------------------------------------------------
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" })
      })
    ]);
    setMaterialCopied(true);
    setTimeout(() => setMaterialCopied(false), 2000);
  } catch (err) {
    console.warn("Clipboard API failed, using fallback...");
    const textArea = document.createElement("textarea");
    textArea.value = plainText;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    
    setMaterialCopied(true);
    setTimeout(() => setMaterialCopied(false), 2000);
  }
};
 console.log(invoiceGeneralData,'invoiceGeneralData');
return (
  <div className="invoice_body container-fluid">
    <div className="row">
      <div className="col-12 image-container">
        <img
          className="invoicelogo"
          alt="Elanco Global R&D Study Material Logistics Hub"
          src={logo}
        />
      </div>
    </div>
    <div className="row">
      <InvoiceItemSize4 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.invoiceShipperAddress")}{invoiceGeneralData?.shipper_details &&
          <Tooltip title={shipperCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => {
                const text = (invoiceGeneralData?.shipper_details || "")?.replace(/,/g, "");
                navigator.clipboard.writeText(text);
                setShipperCopied(true);
                setTimeout(() => setShipperCopied(false), 2000);
              }}
              style={{ color: shipperCopied ? "#2e7d32" : "#969ca1" }}
            >
              {shipperCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          name="shipper_details"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.shipper_details}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize4>
      <InvoiceItemSize4 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.invoiceShipmentDetails")}{invoiceGeneralData?.shipment_details &&
          <Tooltip title={shipmentCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => {
                const text = (invoiceGeneralData?.shipment_details || "")?.replace(/,/g, "");
                navigator.clipboard.writeText(text);
                setShipmentCopied(true);
                setTimeout(() => setShipmentCopied(false), 2000);
              }}
              style={{ color: shipmentCopied ? "#2e7d32" : "#969ca1" }}
            >
              {shipmentCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          name="shipment_details"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.shipment_details}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize4>
    </div>
    <div className="row">
      <InvoiceItemSize4
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {t("invoice.reciver")}{invoiceGeneralData?.recipient_details &&
            <Tooltip title={recipientCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
              <IconButton
                size="small"
                className="no-print"
                onClick={() => {
                  const text = (invoiceGeneralData?.recipient_details || "")?.replace(/,/g, "");
                  navigator.clipboard.writeText(text);
                  setRecipientCopied(true);
                  setTimeout(() => setRecipientCopied(false), 2000);
                }}
                style={{ color: recipientCopied ? "#2e7d32" : "#969ca1" }}
              >
                {recipientCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>}
          </span>
        }>
        <TextareaAutosize
          name="recipient_details"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.recipient_details}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize4>
      <InvoiceItemSize4 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.Broker")}{invoiceGeneralData?.broker_information && invoiceGeneralData?.broker_information !== "\n" &&
          <Tooltip title={brokerCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => {
                const text = (invoiceGeneralData?.broker_information || "")?.replace(/,/g, "");
                navigator.clipboard.writeText(text);
                setBrokerCopied(true);
                setTimeout(() => setBrokerCopied(false), 2000);
              }}
              style={{ color: brokerCopied ? "#2e7d32" : "#969ca1" }}
            >
              {brokerCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          name="broker_information"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.broker_information}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize4>
    </div>
    <div className="row">
      <InvoiceItemSize4 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.importer_of_record")}{ invoiceGeneralData?.importer_of_record && invoiceGeneralData?.importer_of_record !== "\n\n" && 
          <Tooltip title={importerCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => {
                const text = (invoiceGeneralData?.importer_of_record || "")?.replace(/,/g, "");
                navigator.clipboard.writeText(text);
                setImporterCopied(true);
                setTimeout(() => setImporterCopied(false), 2000);
              }}
              style={{ color: importerCopied ? "#2e7d32" : "#969ca1" }}
            >
              {importerCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          name="importer_of_record"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.importer_of_record}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize4>
      <InvoiceItemSize4 title={""}> </InvoiceItemSize4>
    </div>
    <div className="row">
      <InvoiceItemSize12 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.SPECIAL_INSTRUCTIONS")} {invoiceGeneralData?.special_instructions &&
          <Tooltip title={specialInstructionsCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => {
                const text = (invoiceGeneralData?.special_instructions || "")?.replace(/,/g, "");
                navigator.clipboard.writeText(text);
                setSpecialInstructionsCopied(true);
                setTimeout(() => setSpecialInstructionsCopied(false), 2000);
              }}
              style={{ color: specialInstructionsCopied ? "#2e7d32" : "#969ca1" }}
            >
              {specialInstructionsCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          name="special_instructions"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.special_instructions}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize12>
    </div>
    <div className="row">
      <InvoiceItemSize12 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("invoice.material")}
          <Tooltip title={materialCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
            <IconButton
              size="small"
              className="no-print"
              onClick={handleCopyMaterial}
              style={{ color: materialCopied ? "#2e7d32" : "#969ca1" }}
            >
              {materialCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </span >
      }>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: "100%" }} aria-label="customized table" className="override-mui-letter-spacing">
            <TableHead>
              <TableRow>
                <TableCell className="columnnetquantity">
                  {t("invoice.NET_QUANTITY")}
                </TableCell>
                <TableCell>
                  {t("invoice.ITEM DESCRIPTION AND ELANCO ITEM CODE")}{" "}
                </TableCell>
                <TableCell className="columnnetcoo">
                  {t("invoice.COO")}
                </TableCell>
                <TableCell className="columnnetvalue">
                  {t("invoice.CUSTOMS VALUE")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <If condition={editMode}>
                <Then>
                  <InvoiceMaterialEditTable
                    materialShippingDetails={materialShippingDetails}
                    onChange={onChange}
                    invoiceType={INVOICE_TYPES.USA}
                  />
                  <TableRow key={"shipping_cost"}>
                    <TableCell colSpan={3}>
                      <TextField
                        name="shippingcost_text"
                        margin="normal"
                        variant="outlined"
                        size="small"
                        className="total_value pull-right"
                        onChange={onChange}
                        value={invoiceGeneralData.shippingcost_text}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        name="shipping_cost"
                        margin="normal"
                        variant="outlined"
                        size="small"
                        onChange={onChange}
                        value={invoiceGeneralData.shipping_cost}
                      />
                    </TableCell>
                  </TableRow>
                </Then>
                <Else>
                  {materialShippingDetails?.map((row) => (
                    <TableRow key={row.material_id}>
                      <TableCell className="columnnetquantity">
                        {row.quantity}
                      </TableCell>
                      <TableCell>
                        <TextareaAutosize
                          variant="plain"
                          className="descriptiontextarea"
                          disabled
                        >
                          {row.description}
                        </TextareaAutosize>{" "}
                      </TableCell>
                      <TableCell className="columnnetcoo">
                        {row.country_of_origin}
                      </TableCell>
                      <TableCell className="columnnetvalue">
                        {row.cost}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow key={"shipping_cost"}>
                    <TableCell colSpan={3}>
                      <span className="total_value pull-right">
                        {invoiceGeneralData?.shippingcost_text}
                      </span>
                    </TableCell>
                    <TableCell>{invoiceGeneralData.shipping_cost}</TableCell>
                  </TableRow>
                  <TableRow key={"total"}>
                    <TableCell colSpan={3}>
                      <span className="total_value pull-right">
                        {t("invoice.totalValue")} (
                        {invoiceGeneralData.currency_code})
                      </span>
                    </TableCell>

                    <TableCell>{totalValue}</TableCell>
                  </TableRow>
                </Else>
              </If>
            </TableBody>
          </Table>
        </TableContainer>
      </InvoiceItemSize12 >
      <InvoiceItemSize12 title={
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {t("process_shipping_request.comment")} {invoiceGeneralData?.reviewer_comment &&
            <Tooltip title={commentCopied ? "Copied successfully!" : "Copy to clipboard"} className="no-print">
              <IconButton
                size="small"
                className="no-print"
                onClick={() => {
                  const text = (invoiceGeneralData?.reviewer_comment || "")?.replace(/,/g, " ");
                  navigator.clipboard.writeText(text);
                  setCommentCopied(true);
                  setTimeout(() => setCommentCopied(false), 2000);
                }}
                style={{ color: commentCopied ? "#2e7d32" : "#969ca1" }}
              >
                {commentCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>}
        </span>
      }>
        <TextareaAutosize
          aria-label="minimum height"
          name="reviewer_comment"
          minRows={3}
          className="borderOutlineedit"
          style={{ width: "100%" }}
          value={invoiceGeneralData?.reviewer_comment}
          onChange={onChange}
        />
      </InvoiceItemSize12>
      <InvoiceItemSize12>
        <TextareaAutosize
          name="footer_information"
          margin="normal"
          variant="plain"
          className={editMode ? "borderOutlineedit" : "shippingDetailstextarea"}
          style={{ width: "100%" }}
          value={invoiceGeneralData?.footer_information}
          onChange={onChange}
          disabled={editMode ? false : true}
        />
      </InvoiceItemSize12>
    </div >
  </div >
);
}
